/* eslint-disable react-refresh/only-export-components */
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Navbar from "@components/common/Navbar/Navbar";
import Footer from "@components/common/Footer/Footer";
import Loading from "@components/common/Loading/Loading";
import ProtectedRoute from "@components/ProtectedRoute/ProtectedRoute";

// Direct imports for lightweight/critical pages (included in main bundle)
import LandingPage from "@pages/LandingPage/LandingPage";
import LoginPage from "@pages/LoginPage/LoginPage";
import AboutPage from "@pages/AboutPage/AboutPage";
import MethodologyPage from "@pages/MethodologyPage/MethodologyPage";
import NotFound from "@pages/NotFound/NotFound";

// Lazy imports for heavy/complex pages (separate chunks)
const SimpleExplorer = lazy(() =>
  import("@pages/SimpleExplorer/SimpleExplorer")
);
const AdvancedExplorer = lazy(() =>
  import("@pages/AdvancedExplorer/AdvancedExplorer")
);
const DataDownloadPage = lazy(() =>
  import("@pages/DataDownloadPage/DataDownloadPage")
);

/**
 * Application route constants
 */
export const ROUTES = {
  HOME: "/",
  SIMPLE: "/simple",
  ADVANCED: "/advanced",
  COUNTRY: "/country",
  COUNTRY_GROUPS: "/country-groups",
  METHODOLOGY: "/methodology",
  ABOUT: "/about",
  LOGIN: "/login",
  DOWNLOADS: "/downloads",
};

/**
 * Page wrapper component for static pages (no suspense needed)
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Page content
 */
const StaticPageWrapper = ({ children }) => (
  <div className="page-layout">
    <Navbar />
    <main className="main-content">{children}</main>
    <Footer />
  </div>
);

/**
 * Page wrapper component for lazy-loaded pages (with suspense)
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Page content
 */
const LazyPageWrapper = ({ children }) => (
  <div className="page-layout">
    <Navbar />
    <main className="main-content">
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </main>
    <Footer />
  </div>
);

/**
 * Preload heavy components for better UX
 * Call this function on landing page or after initial render
 */
export const preloadHeavyRoutes = () => {
  // Preload explorers after a short delay
  setTimeout(() => {
    import("@pages/SimpleExplorer/SimpleExplorer");
    import("@pages/AdvancedExplorer/AdvancedExplorer");
  }, 2000);

  // Preload downloads page with longer delay (less priority)
  setTimeout(() => {
    import("@pages/DataDownloadPage/DataDownloadPage");
  }, 5000);
};

/**
 * Main application router component with optimized route definitions
 */
const AppRouter = () => {
  return (
    <Routes>
      {/* Static routes - no lazy loading needed */}
      <Route
        path={ROUTES.HOME}
        element={
          <StaticPageWrapper>
            <LandingPage />
          </StaticPageWrapper>
        }
      />

      <Route path={ROUTES.LOGIN} element={<LoginPage />} />

      <Route
        path={ROUTES.METHODOLOGY}
        element={
          <StaticPageWrapper>
            <MethodologyPage />
          </StaticPageWrapper>
        }
      />

      <Route
        path={ROUTES.ABOUT}
        element={
          <StaticPageWrapper>
            <AboutPage />
          </StaticPageWrapper>
        }
      />

      {/* Lazy routes - heavy components with code splitting */}
      <Route
        path={ROUTES.SIMPLE}
        element={
          <LazyPageWrapper>
            <SimpleExplorer />
          </LazyPageWrapper>
        }
      />

      <Route
        path={ROUTES.ADVANCED}
        element={
          <ProtectedRoute>
            <LazyPageWrapper>
              <AdvancedExplorer />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.DOWNLOADS}
        element={
          <ProtectedRoute>
            <LazyPageWrapper>
              <DataDownloadPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />

      {/* Legacy route redirects */}
      <Route
        path="/explorer"
        element={<Navigate to={ROUTES.SIMPLE} replace />}
      />
      <Route path="/data" element={<Navigate to={ROUTES.SIMPLE} replace />} />
      <Route
        path="/research"
        element={<Navigate to={ROUTES.METHODOLOGY} replace />}
      />
      <Route
        path="/meth"
        element={<Navigate to={ROUTES.METHODOLOGY} replace />}
      />

      {/* 404 - Static route */}
      <Route
        path="*"
        element={
          <StaticPageWrapper>
            <NotFound />
          </StaticPageWrapper>
        }
      />
    </Routes>
  );
};

export default AppRouter;

/**
 * Hook to access route constants
 * @returns {Object} Route constants object
 */
export const useRoutes = () => ROUTES;

/**
 * Generate navigation items for menu components
 * @returns {Array} Array of navigation item objects
 */
export const getNavigationItems = () => [
  {
    label: "Home",
    path: ROUTES.HOME,
    description: "ISCRI landing page",
    isStatic: true,
  },
  {
    label: "Simple Explorer",
    path: ROUTES.SIMPLE,
    description: "Pre-configured ISCRI visualization",
    isStatic: false,
  },
  {
    label: "Advanced Explorer",
    path: ROUTES.ADVANCED,
    description: "Flexible ISCRI analysis tools",
    isStatic: false,
  },
  {
    label: "Country Summary",
    path: ROUTES.COUNTRY,
    description: "Country-level ISCRI aggregations",
    isStatic: false,
  },
  {
    label: "Country Groups",
    path: ROUTES.COUNTRY_GROUPS,
    description: "Regional ISCRI summaries",
    isStatic: false,
  },
  {
    label: "Methodology",
    path: ROUTES.METHODOLOGY,
    description: "Research and methodology",
    isStatic: true,
  },
  {
    label: "About",
    path: ROUTES.ABOUT,
    description: "About SKEMA and ISCRI",
    isStatic: true,
  },
  {
    label: "Downloads",
    path: ROUTES.DOWNLOADS,
    description: "Database downloads (password protected)",
    isStatic: false,
  },
];
